import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useAudit } from "./AuditContext";
import { tapFeedback } from "./haptics";
import { PresenterFloorShadow } from "./PresenterFloorShadow";
import { useIsMobileAudit } from "./useIsMobileAudit";

/**
 * Rehearsal Room (Room 01 — Message)
 * ==================================
 * Two-phase experience:
 *   1. ARRIVE   — user sees the wide rehearsal room; a "Step to the board"
 *                 button zooms them into the whiteboard close-up.
 *   2. INTERACT — the mockup layout: 12 sticky notes on the left, two
 *                 coloured buckets on the right, presenter far-left.
 *
 * The transition is a soft cinematic zoom (Framer Motion) between two
 * background images: /audit/room_wide.webp → /audit/bg_message.webp.
 * A "Back to room" button reverses the zoom.
 *
 * Layout inside INTERACT phase (per spec, exact CSS):
 *   • Presenter: absolute; left:-10%; bottom:0; height:92%; z-index:10
 *   • Bubble:    absolute; top:20; left:150; z-index:100; max-width:300
 *   • Board:     flex row; gap:40; margin-left:20%
 *       - Left col: grid 2 cols; gap:10
 *       - Right col: flex col; gap:20; width:40%; align-self:center
 */

// ---------------------------------------------------------------------------
// Content
// ---------------------------------------------------------------------------
const MESSAGE_NOTES = [
  { id: "audience",   label: "Audience",           hint: "Who am I speaking to?" },
  { id: "goal",       label: "Goal",               hint: "What am I trying to achieve?" },
  { id: "think",      label: "Think",              hint: "What should they think differently?" },
  { id: "feel",       label: "Feel",               hint: "What should they feel?" },
  { id: "do",         label: "Do",                 hint: "What should they do afterwards?" },
  { id: "coreIdea",   label: "Core Idea",          hint: "What is the one idea to remember?" },
  { id: "whyNow",     label: "Why Now",            hint: "Why does this matter now?" },
  { id: "stakes",     label: "Stakes",             hint: "What matters to them?" },
  { id: "whatToSay",  label: "What I Want to Say", hint: "What do I want to communicate?" },
  { id: "evidence",   label: "Evidence",           hint: "What facts or data do I need to include?" },
  { id: "coverage",   label: "Content Coverage",   hint: "What information must I cover?" },
  { id: "time",       label: "Time",               hint: "How much time do I have?" },
];

const INTRO_STEPS = [
  "Before you build a message\u2026",
  "\u2026decide what deserves your attention first.",
  "You\u2019ll see a set of notes on the board.",
  "Move five into \u2018For Now\u2019.",
  "The rest can wait.",
  "When you\u2019re ready, begin.",
];

const SUCCESS_STEPS = [
  "Good. That helps me understand what was guiding your attention.",
  "Now let\u2019s see how you turn those priorities into a structure.",
  "Let\u2019s build the flow.",
];

const ARRIVE_STEPS = [
  "Welcome to the rehearsal room of InterAct Globally.",
  "We\u2019re going to explore together how you naturally approach a presentation.",
  "There are no perfect presentation styles here.",
  "Just make the choices that feel natural to you.",
  "Ready? Let\u2019s start with your message.",
];

const REFLECTION_QUESTION = "What guided your choices most?";
const REFLECTION_OPTIONS = [
  { id: "audience",    label: "The audience",          hint: "What they need from me" },
  { id: "outcome",     label: "The outcome",           hint: "What I want the presentation to achieve" },
  { id: "message",     label: "The message",           hint: "What I most want to say" },
  { id: "content",     label: "The content",           hint: "What information I need to cover" },
  { id: "constraints", label: "Practical constraints", hint: "Time and what I can realistically include" },
];

const FOR_NOW_MAX = 5;
const NOTE_ROTATIONS = [-2.5, 1.4, -1.2, 2.1, -0.6, 1.8, -2.0, 0.7, -1.7, 2.3, -0.9, 1.1];

// Soft mask hides the two decorative "spotlights" at the top of every
// sprite AND the baked-in floor shadow ellipse under the feet.
const PRESENTER_MASK =
  "linear-gradient(to bottom, transparent 0%, black 5%, black 95%, transparent 100%)";

// ---------------------------------------------------------------------------
// Note component
// ---------------------------------------------------------------------------
const Pin = () => (
  <span
    aria-hidden="true"
    className="absolute -top-1.5 left-1/2 -translate-x-1/2"
    style={{ width: 12, height: 12, zIndex: 2 }}
  >
    <span
      className="block w-full h-full rounded-full"
      style={{
        background: "radial-gradient(circle at 35% 30%, #ff5b4c 0%, #c62b1a 55%, #7a1206 100%)",
        boxShadow: "0 1.5px 2px rgba(0,0,0,0.45), inset 0 -1px 1px rgba(0,0,0,0.35)",
      }}
    />
  </span>
);

function Note({ note, index, interactive, onDragStart, onTap }) {
  const rotation = NOTE_ROTATIONS[index % NOTE_ROTATIONS.length];
  return (
    <button
      type="button"
      draggable={interactive}
      onDragStart={(e) => {
        if (!interactive) return;
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", note.id);
        onDragStart?.(note.id);
      }}
      onClick={() => interactive && onTap?.(note.id)}
      disabled={!interactive}
      data-testid={`note-${note.id}`}
      className={[
        "relative text-left w-full min-h-[92px] px-3 py-2.5",
        "transition-transform duration-200",
        interactive ? "cursor-grab active:cursor-grabbing hover:-translate-y-0.5" : "cursor-default",
      ].join(" ")}
      style={{
        background: "linear-gradient(180deg, #FFEA6A 0%, #F7C93A 100%)",
        boxShadow: "0 6px 14px rgba(60,40,10,0.28), inset 0 -3px 0 rgba(0,0,0,0.06)",
        transform: `rotate(${rotation}deg)`,
        zIndex: 20,
      }}
    >
      <Pin />
      <p className="font-serif text-[13px] leading-tight text-ink pt-1">
        {note.label}
      </p>
      {note.hint && (
        <p className="mt-1 font-body text-[10px] leading-tight text-ink/75">
          {note.hint}
        </p>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Bucket component (drop target)
// ---------------------------------------------------------------------------
function Bucket({ id, title, count, cap, notes, indexOf, onDrop, onTap, interactive, bgFrom, bgTo, testId }) {
  const capReached = typeof cap === "number" && count >= cap;
  return (
    <div
      onDragOver={(e) => interactive && !capReached && e.preventDefault()}
      onDrop={(e) => {
        if (!interactive) return;
        e.preventDefault();
        const nid = e.dataTransfer.getData("text/plain");
        if (nid) onDrop(nid, id);
      }}
      data-testid={testId}
      className="relative w-full px-4 py-3 flex flex-col"
      style={{
        background: `linear-gradient(160deg, ${bgFrom} 0%, ${bgTo} 100%)`,
        boxShadow: "inset 0 2px 6px rgba(0,0,0,0.18), 0 4px 10px rgba(0,0,0,0.18)",
        minHeight: 160,
        zIndex: 15,
      }}
    >
      <div className="flex items-baseline justify-between mb-2">
        <p
          className="font-mono uppercase tracking-[0.24em] text-white"
          style={{ fontSize: 11, textShadow: "0 1px 2px rgba(0,0,0,0.45)" }}
        >
          {title}
        </p>
        {typeof cap === "number" && (
          <p
            data-testid={`${testId}-counter`}
            className="font-mono text-white"
            style={{ fontSize: 12, textShadow: "0 1px 2px rgba(0,0,0,0.45)" }}
          >
            {count}/{cap}
          </p>
        )}
      </div>
      <div
        className="grid gap-2 items-start content-start"
        style={{ gridTemplateColumns: "repeat(3, 1fr)" }}
      >
        {notes.map((n) => (
          <Note
            key={n.id}
            note={n}
            index={indexOf(n.id)}
            interactive={interactive}
            onTap={onTap}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rehearsal Room
// ---------------------------------------------------------------------------
export function RehearsalRoom() {
  const {
    answers,
    setRoomAnswer,
    setTagsForRoom,
    setCanProceed,
    setOverrideMessage,
    setReflection,
    selectedPresenter,
    overrideMessage,
    tourActive,
    onboardingCompleted,
    startTour,
  } = useAudit();
  const isMobile = useIsMobileAudit();

  // Phase state — "arrive" (wide room, before zoom) or "interact" (whiteboard).
  const [phase, setPhase] = useState("arrive");
  // Step through the intro bubbles in the arrive scene.
  const [arriveStep, setArriveStep] = useState(0);
  const arriveIsLast = arriveStep >= ARRIVE_STEPS.length - 1;

  // Quick UI Tour — plays once, right after the welcome dialogue finishes
  // and before the Room 01 task begins.
  useEffect(() => {
    if (phase === "arrive" && arriveIsLast && !onboardingCompleted) startTour();
  }, [phase, arriveIsLast, onboardingCompleted, startTour]);

  const forNow   = answers.message?.forNow   || [];
  const forLater = answers.message?.forLater || [];
  const source   = MESSAGE_NOTES
    .filter((n) => !forNow.includes(n.id) && !forLater.includes(n.id))
    .map((n) => n.id);

  const indexOf = (id) => MESSAGE_NOTES.findIndex((n) => n.id === id);
  const noteById = (id) => MESSAGE_NOTES.find((n) => n.id === id);

  const introStep   = answers.message?.introStep   ?? 0;
  const successStep = answers.message?.successStep ?? 0;
  const introDone      = introStep >= INTRO_STEPS.length;
  const placed         = forNow.length === FOR_NOW_MAX;
  const reflectionPick = answers.message?.reflection?.selected ?? null;
  const inReflectPick  = phase === "interact" && introDone && placed && !reflectionPick;
  // Once the task is complete the board clears — reflection + closing beats
  // play on a clean stage with only the Presenter.
  const taskHidden     = introDone && placed;
  const successDone    = successStep >= SUCCESS_STEPS.length;
  const inCatcher      = phase === "interact" && (!introDone || (placed && !!reflectionPick && !successDone));

  // Sync bubble + continue gate with current phase + interact sub-phase.
  useEffect(() => {
    if (phase === "arrive") {
      setOverrideMessage(ARRIVE_STEPS[arriveStep]);
      setCanProceed(false);
      return;
    }
    if (!introDone) {
      setOverrideMessage(INTRO_STEPS[introStep]);
      setCanProceed(false);
      return;
    }
    if (!placed) {
      setOverrideMessage("");
      setCanProceed(false);
      return;
    }
    if (!reflectionPick) {
      setOverrideMessage(REFLECTION_QUESTION);
      setCanProceed(false);
      return;
    }
    if (!successDone) {
      setOverrideMessage(SUCCESS_STEPS[successStep]);
      setCanProceed(false);
      return;
    }
    setOverrideMessage(SUCCESS_STEPS[SUCCESS_STEPS.length - 1]);
    setCanProceed(true);
  }, [phase, arriveStep, introStep, introDone, placed, reflectionPick, successStep, successDone, setCanProceed, setOverrideMessage]);

  useEffect(() => {
    if (!placed && successStep > 0) {
      setRoomAnswer("message", { successStep: 0 });
    }
  }, [placed, successStep, setRoomAnswer]);

  const move = (noteId, targetZone) => {
    const currentZone = forNow.includes(noteId)
      ? "forNow"
      : forLater.includes(noteId)
      ? "forLater"
      : "source";
    if (currentZone === targetZone) return;
    if (targetZone === "forNow" && forNow.length >= FOR_NOW_MAX) return;
    tapFeedback();

    let nextForNow   = forNow.filter((id) => id !== noteId);
    let nextForLater = forLater.filter((id) => id !== noteId);
    if (targetZone === "forNow")   nextForNow   = [...nextForNow,   noteId];
    if (targetZone === "forLater") nextForLater = [...nextForLater, noteId];

    const patch = { forNow: nextForNow, forLater: nextForLater };
    if (!introDone) patch.introStep = INTRO_STEPS.length;
    setRoomAnswer("message", patch);
    setTagsForRoom("msg", nextForNow.map((cid) => `msg:${cid}`));
  };

  const tap = (noteId) => {
    if (forNow.includes(noteId))  return move(noteId, "forLater");
    if (forLater.includes(noteId)) return move(noteId, "source");
    if (forNow.length < FOR_NOW_MAX) return move(noteId, "forNow");
    return move(noteId, "forLater");
  };

  const advance = () => {
    if (!introDone) {
      setRoomAnswer("message", { introStep: introStep + 1 });
      return;
    }
    if (placed && reflectionPick && !successDone) {
      setRoomAnswer("message", { successStep: successStep + 1 });
    }
  };

  const pickReflection = (optionId) => {
    if (!inReflectPick) return;
    tapFeedback();
    const tag = `message_reflection:${optionId}`;
    setRoomAnswer("message", { reflection: { selected: optionId, tag }, successStep: 0 });
    setReflection("message", { question: REFLECTION_QUESTION, selected: optionId, tag });
    setTagsForRoom("message_reflection", [tag]);
  };

  const source_drop = (nid) => move(nid, "source");
  const interactive = phase === "interact" && introDone;
  // While the user is placing notes (has started the task but hasn't yet
  // completed all placements), shrink the presenter into a listening pose —
  // same feel as Rooms 03 & 04. He grows back once the task succeeds.
  const isListening = phase === "interact" && introDone && !placed;

  const presenter = selectedPresenter;
  const pose = phase === "arrive"
    ? "welcomeArms"
    : (isListening ? "listening" : (placed ? "presenting" : "presentingAlt"));
  const poseFile = presenter?.poses?.[pose] || presenter?.poses?.neutral;
  const presenterSrc = presenter ? `${presenter.avatarBase}/${poseFile}` : "";

  const totalCatcherSteps = !introDone ? INTRO_STEPS.length : SUCCESS_STEPS.length;
  const currentCatcherStep = !introDone ? introStep + 1 : successStep + 1;

  const isArrive = phase === "arrive";
  const CINEMATIC = { duration: 1.2, ease: [0.22, 1, 0.36, 1] };

  return (
    <div
      className="absolute inset-0 overflow-hidden"
      data-testid="rehearsal-room"
      style={{ background: "#141310" }}
    >
      {/* Background layer — WIDE room (arrive) */}
      <motion.div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          backgroundImage: "url(/audit/room_wide.webp)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        animate={{
          opacity: isArrive ? 1 : 0,
          scale: isArrive ? 1 : 2.8,
          x: isArrive ? "0%" : "-8%",
          y: isArrive ? "0%" : "-18%",
        }}
        initial={false}
        transition={CINEMATIC}
        data-testid="rehearsal-bg-wide"
      />

      {/* Background layer — WHITEBOARD close-up (interact) */}
      <motion.div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          backgroundImage: "url(/audit/bg_message.webp)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        animate={{
          opacity: isArrive ? 0 : 1,
          scale: isArrive ? 1.08 : 1,
        }}
        initial={false}
        transition={CINEMATIC}
        data-testid="rehearsal-bg-message"
      />

      {/* Presenter — position + size depends on phase. The new 1400px asset
          set (Sep 2026) is tightly cropped: only ~6.2% blank padding above
          AND below the actual figure (vs ~31.6% on the old set), so pure
          height% growth barely needs any bottom compensation now:
            - arrive (welcomeArms, full body): bottom = -(0.062 × height)
              keeps his feet glued to the floor as he gets bigger.
            - presenting (whiteboard, close-up): a taller box crops his
              legs off the bottom of frame (via this wrapper's own
              overflow-hidden) while his head stays near the top, close to
              the speech bubble — a chest-up "close-up" shot.
            - listening (task in progress): a smaller, grounded figure off
              to the side. */}
      {presenter && (
        <motion.div
          className={isArrive ? "absolute" : "absolute overflow-hidden"}
          animate={{
            left: isArrive ? "calc(24% - 70px)" : (isListening ? "calc(-10% + 20px)" : "calc(-25% + 165px)"),
            bottom: isArrive ? "-6.2%" : (isListening ? "-3.75%" : "calc(-42.315% + 30px)"),
            // The "presenting" (whiteboard close-up) height was originally a
            // fixed 960px, tuned against a ~900px-tall desktop viewport
            // (960/900 ≈ 106.7%). On short mobile-landscape viewports
            // (~375-430px tall) that fixed px value over-zoomed the
            // presenter ~2.5x, pushing his head out of frame and his torso
            // wide enough to cover the sticky notes. Expressing it as the
            // SAME 106.7% ratio (relative to this room's own full-height
            // container) reproduces the identical desktop crop while
            // scaling correctly on any viewport height.
            height: isArrive ? "100%" : (isListening ? "60.5%" : "106.7%"),
          }}
          initial={false}
          transition={CINEMATIC}
          style={{ zIndex: 10 }}
          data-testid="rehearsal-presenter"
        >
          <PresenterFloorShadow bottom="6.2%" />
          <AnimatePresence initial={false} mode="popLayout">
            <motion.img
              key={pose}
              src={presenterSrc}
              alt={presenter.name}
              draggable={false}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              initial={{ opacity: 0, y: 4, scaleX: pose === "listening" ? -1 : 1 }}
              animate={{ opacity: 1, y: 0, scaleX: pose === "listening" ? -1 : 1 }}
              exit={{ opacity: 0, y: -4, scaleX: pose === "listening" ? -1 : 1 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="h-full w-auto select-none block"
              style={{
                mixBlendMode: "multiply",
                filter: "brightness(0.95) sepia(0.1)",
                imageRendering: "-webkit-optimize-contrast",
                WebkitMaskImage: PRESENTER_MASK,
                maskImage: PRESENTER_MASK,
              }}
              data-testid={`rehearsal-presenter-img-${pose}`}
            />
          </AnimatePresence>
        </motion.div>
      )}


      {/* Speech bubble — position depends on phase. Hidden while the user
          is actively placing notes (isListening) so they can focus on the
          task, matching the coaching→listening feel of Rooms 03 & 04. */}
      <AnimatePresence initial={false}>
        {overrideMessage && !isListening ? (
          <motion.div
            key={`${phase}:${overrideMessage}`}
            initial={{ opacity: 0, y: 6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="absolute"
            style={
              isArrive
                ? { top: "calc(18% - 45px)", left: "44%", zIndex: 100, maxWidth: 620 }
                : { top: 5, left: 150, zIndex: 100, maxWidth: 620 }
            }
            data-testid="rehearsal-bubble"
          >
            <div
              className="relative bg-white text-[#1B1B18] px-9 py-6 shadow-[0_16px_40px_rgba(0,0,0,0.4)]"
              style={{ borderRadius: 34, transform: "translate(45px, 15px)" }}
            >
              <p className="font-serif text-2xl leading-snug">{overrideMessage}</p>
              {/* The arrival/"beginning" bubble is now pulled tight against
                  the presenter on mobile (see audit-mobile.css) — a
                  bottom-left tail no longer points anywhere meaningful at
                  that position, so it's dropped for a plain bubble there. */}
              {!(isMobile && isArrive) && (
              <span
                aria-hidden="true"
                className="absolute"
                style={{
                  left: 28,
                  bottom: -16,
                  width: 0,
                  height: 0,
                  borderLeft: "14px solid transparent",
                  borderRight: "8px solid transparent",
                  borderTop: "20px solid #ffffff",
                  filter: "drop-shadow(0 4px 4px rgba(0,0,0,0.15))",
                }}
              />
              )}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* Corner mark — top-right (both phases) */}
      <div
        className="absolute right-6 top-5 flex flex-col items-end pointer-events-none"
        style={{ zIndex: 50 }}
        data-testid="rehearsal-cornermark"
      >
        <p className="font-serif italic text-ink text-lg md:text-2xl leading-none tracking-wide drop-shadow-[0_2px_6px_rgba(255,240,210,0.4)]">
          InterAct Globally
        </p>
        <p className="font-hand text-terracotta/90 text-base md:text-xl leading-tight mt-1 drop-shadow-[0_2px_6px_rgba(255,240,210,0.4)]">
          the rehearsal room
        </p>
      </div>

      {/* ARRIVE phase — tap-anywhere to advance the intro bubbles,
          then a prominent CTA on the last line that zooms to the board. */}
      <AnimatePresence>
        {isArrive && (
          <>
            {/* Full-scene tap catcher — advances the bubble until the last one */}
            {!arriveIsLast && (
              <motion.button
                key="arrive-catcher"
                type="button"
                onClick={() => setArriveStep((s) => Math.min(ARRIVE_STEPS.length - 1, s + 1))}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                aria-label="Continue"
                data-testid="arrive-advance"
                className="absolute inset-0 cursor-pointer bg-transparent pointer-events-auto"
                style={{ zIndex: 80 }}
              >
                <span
                  aria-hidden="true"
                  className="absolute bottom-16 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.28em] text-ink/60 animate-pulse"
                >
                  tap to continue &nbsp;·&nbsp; {arriveStep + 1} / {ARRIVE_STEPS.length}
                </span>
              </motion.button>
            )}

            {/* Final CTA — appears on the last bubble (after the UI tour) */}
            {arriveIsLast && !tourActive && (
              <motion.button
                key="enter-btn"
                type="button"
                onClick={() => setPhase("interact")}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                transition={{ duration: 0.5, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className="absolute font-mono uppercase tracking-[0.24em] text-base md:text-lg px-10 py-5 bg-terracotta text-paper hover:bg-ink transition-colors shadow-[0_16px_40px_rgba(0,0,0,0.45)] ring-2 ring-paper/40 hover:ring-paper"
                style={{
                  left: "50%",
                  bottom: "calc(10% + 180px)",
                  transform: "translateX(-50%)",
                  zIndex: 90,
                  letterSpacing: "0.24em",
                }}
                data-testid="rehearsal-enter-btn"
              >
                → Step to the audit ←
              </motion.button>
            )}
          </>
        )}
      </AnimatePresence>

      {/* INTERACT phase — whiteboard grid + back button */}
      <AnimatePresence>
        {!isArrive && (
          <motion.div
            key="interact-layer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, delay: 0.35 }}
            className="absolute inset-0"
            data-testid="rehearsal-interact-layer"
          >
            {/* Back-to-room button (top-left, above content) — hidden once the task is done */}
            {!taskHidden && (
            <button
              type="button"
              onClick={() => setPhase("arrive")}
              className="absolute font-mono uppercase tracking-[0.22em] text-[10px] px-4 py-2 bg-ink/70 text-paper hover:bg-ink transition-colors backdrop-blur-sm"
              style={{ left: 20, bottom: 90, zIndex: 90 }}
              data-testid="rehearsal-back-btn"
            >
              ← Back to room
            </button>
            )}

            {/* Whiteboard grid container — cleared during the reflection phase.
                On mobile, stays hidden until the intro beats finish so the
                room opens on just the Presenter + background, cards revealed
                only once the introduction is complete. */}
            <AnimatePresence initial={false}>
            {!taskHidden && (!isMobile || introDone) && (
            <motion.div
              exit={{ opacity: 0, transition: { duration: 0.45 } }}
              className="relative w-full h-full"
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-start",
                gap: 40,
                marginLeft: "20%",
                paddingTop: "4%",
                paddingRight: "3%",
                zIndex: 15,
              }}
              data-testid="rehearsal-board"
            >
              {/* LEFT COLUMN — grid of available notes */}
              <div
                onDragOver={(e) => interactive && e.preventDefault()}
                onDrop={(e) => {
                  if (!interactive) return;
                  e.preventDefault();
                  const nid = e.dataTransfer.getData("text/plain");
                  if (nid) source_drop(nid);
                }}
                data-testid="rehearsal-source"
                className="flex-1"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 10,
                  maxWidth: "40%",
                }}
              >
                {source.map((sid) => {
                  const n = noteById(sid);
                  return (
                    <Note
                      key={n.id}
                      note={n}
                      index={indexOf(n.id)}
                      interactive={interactive}
                      onTap={tap}
                    />
                  );
                })}
              </div>

              {/* RIGHT COLUMN — two buckets */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 20,
                  width: "calc(40% - 15px)",
                  alignSelf: "center",
                }}
                data-testid="rehearsal-buckets"
              >
                <Bucket
                  id="forNow"
                  title="For Now"
                  count={forNow.length}
                  cap={FOR_NOW_MAX}
                  notes={forNow.map(noteById)}
                  indexOf={indexOf}
                  onDrop={move}
                  onTap={tap}
                  interactive={interactive}
                  bgFrom="#7C8A4E"
                  bgTo="#5C6A38"
                  testId="zone-forNow"
                />
                <Bucket
                  id="forLater"
                  title="For Later"
                  count={forLater.length}
                  notes={forLater.map(noteById)}
                  indexOf={indexOf}
                  onDrop={move}
                  onTap={tap}
                  interactive={interactive}
                  bgFrom="#D4A85A"
                  bgTo="#B08838"
                  testId="zone-forLater"
                />
              </div>
            </motion.div>
            )}
            </AnimatePresence>

            {/* Reflection cards — after the task, before the closing beats */}
            <AnimatePresence>
              {inReflectPick && (
                <motion.div
                  key="message-reflection-cards"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute grid grid-cols-2 gap-4"
                  style={{ top: "26%", left: "26%", right: "5%", zIndex: 60 }}
                  data-testid="message-reflection-cards"
                >
                  {REFLECTION_OPTIONS.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => pickReflection(opt.id)}
                      data-testid={`message-reflection-${opt.id}`}
                      className="text-left px-6 py-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_14px_36px_rgba(0,0,0,0.25)]"
                      style={{
                        background: "rgba(242, 235, 218, 0.92)",
                        border: "1px solid rgba(90,60,25,0.16)",
                        boxShadow: "0 6px 18px rgba(0,0,0,0.16)",
                      }}
                    >
                      <p className="font-serif text-xl md:text-2xl leading-snug text-ink">{opt.label}</p>
                      <p className="mt-1 font-body text-sm text-ink/70">&ldquo;{opt.hint}&rdquo;</p>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Intro/success advancer — steps the bubble */}
            {inCatcher && (
              <button
                type="button"
                onClick={advance}
                data-testid="intro-advance"
                aria-label="Continue"
                className="absolute inset-0 cursor-pointer bg-transparent pointer-events-auto"
                style={{ zIndex: 88 }}
              >
                <span
                  aria-hidden="true"
                  className="absolute bottom-24 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.28em] text-ink/60 animate-pulse"
                >
                  tap to continue &nbsp;·&nbsp; {currentCatcherStep} / {totalCatcherSteps}
                </span>
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default RehearsalRoom;
